import { isVisitOpen, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { useNearbyStore } from '../../data/geo';
import { setListStore } from '../../data/lists';
import { useStore, useStoreResolver } from '../../data/stores';
import { db } from '../../db/db';
import type { ListItemRow } from '../../db/types';
import { requestGeo, setGeoEnabled, useGeoStatus } from '../../lib/geo';

interface Props {
  listId: Id;
  storeId: Id | null;
  items: ListItemRow[];
}

const HINT_KEY = 'odhaczka.geoHint';

function hintDismissed(): boolean {
  try {
    return localStorage.getItem(HINT_KEY) === '1';
  } catch {
    return false;
  }
}

const cardCls = 'm-4 rounded-2xl border border-brand-100 bg-brand-50 p-4 dark:border-brand-800/50 dark:bg-brand-800/20';
const smallBtn = 'min-h-11 shrink-0 rounded-xl px-4 font-medium';

/**
 * Tryb sklepu: „Jesteś w sklepie X? Zmień sklep” (jedno dotknięcie), gdy lokalizacja wskazuje inny sklep niż listy.
 * Tylko przed pierwszym odhaczeniem w wizycie — później zmiana sklepu zamknęłaby wizytę, a zły sklep
 * nauczyłby się kawałka trasy. Nic nie zmienia się samo. Lista bez sklepu przy wyłączonej lokalizacji
 * dostaje zachętę do jej włączenia.
 */
export function NearbyStoreCard({ listId, storeId, items }: Props) {
  const geo = useGeoStatus();
  const near = useNearbyStore();
  const resolve = useStoreResolver();
  const nearest = useStore(near.nearest?.storeId ?? null);
  const visits = useLiveQuery(() => db.visits.where('listId').equals(listId).toArray(), [listId]);
  const [dismissed, setDismissed] = useState(hintDismissed);
  const [enabling, setEnabling] = useState(false);
  const [justEnabled, setJustEnabled] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  if (visits === undefined || resolve === undefined) return null;
  // Ktoś (ja albo domownik) już odhacza w tej liście — sklep zostaje, jaki jest.
  const started = visits.some((v) => isVisitOpen(v, items, Date.now()));

  if (nearest && !started && (storeId === null || resolve(storeId) !== nearest.id)) {
    return (
      <div className={`${cardCls} flex items-center gap-3`}>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            <span aria-hidden>📍 </span>Jesteś w sklepie {nearest.name || 'Bez nazwy'}?
          </p>
          {nearest.address && <p className="mt-0.5 truncate text-sm text-stone-600 dark:text-stone-400">{nearest.address}</p>}
        </div>
        <button
          type="button"
          onClick={() => void setListStore(listId, nearest.id).catch(() => {})}
          className={`${smallBtn} bg-brand-700 text-white active:bg-brand-800`}
        >
          {storeId ? 'Zmień sklep' : 'Wybierz'}
        </button>
      </div>
    );
  }

  if (justEnabled && geo.active && near.position && !near.nearest && storeId === null) {
    return (
      <p className={`${cardCls} text-sm text-stone-600 dark:text-stone-400`}>
        Lokalizacja włączona, ale nie znam sklepu w pobliżu. Wybierz sklep poniżej — w trakcie zakupów zapamiętam, gdzie jest.
      </p>
    );
  }

  if (storeId !== null || dismissed || !geo.supported || geo.active || geo.permission === undefined || geo.permission === 'denied') return null;

  // Gest użytkownika — tu wolno zapytać o zgodę.
  const enable = async () => {
    setEnabling(true);
    setInfo(null);
    try {
      const res = await requestGeo();
      setGeoEnabled(res.status === 'granted');
      if (res.status === 'granted') setJustEnabled(true);
      else setInfo('Brak zgody na lokalizację — sklep wybierzesz ręcznie.');
    } finally {
      setEnabling(false);
    }
  };
  const dismiss = () => {
    try {
      localStorage.setItem(HINT_KEY, '1');
    } catch {
      // bez zapisu karta wróci przy następnym otwarciu listy
    }
    setDismissed(true);
  };

  return (
    <div className={cardCls}>
      <p className="font-semibold">
        <span aria-hidden>📍 </span>Włącz lokalizację, a aplikacja sama podpowie sklep
      </p>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">Można to zmienić w każdej chwili w zakładce Dom.</p>
      {info && <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">{info}</p>}
      <div className="mt-3 flex gap-2">
        <button type="button" disabled={enabling} onClick={() => void enable()} className={`${smallBtn} bg-brand-700 text-white active:bg-brand-800 disabled:opacity-50`}>
          {enabling ? 'Włączam…' : 'Włącz lokalizację'}
        </button>
        <button type="button" onClick={dismiss} className={`${smallBtn} text-stone-600 active:bg-brand-100 dark:text-stone-300 dark:active:bg-stone-800`}>
          Nie teraz
        </button>
      </div>
    </div>
  );
}
